import { createContext, type FormEvent, type ReactNode, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";

const SaveBoundaryContext = createContext(false);

export type SaveBoundaryProps = {
  saving?: boolean;
  children: ReactNode;
  className?: string;
};

export const useSaveBoundaryDisabled = (disabled = false): boolean => {
  const boundaryDisabled = useContext(SaveBoundaryContext);
  return disabled || boundaryDisabled;
};

export const SaveBoundary = ({ saving = false, children, className }: SaveBoundaryProps) => {
  const parentDisabled = useContext(SaveBoundaryContext);
  const disabled = saving || parentDisabled;
  const rootRef = useRef<HTMLDivElement>(null);
  const focusedRef = useRef<HTMLElement | null>(null);
  const wasDisabledRef = useRef(disabled);

  useLayoutEffect(() => {
    const root = rootRef.current;
    const active = root?.ownerDocument.activeElement;
    const body = root?.ownerDocument.body;
    if (disabled && active !== body && !root?.contains(active ?? null)) focusedRef.current = null;
    // Inert removes focus; restore it only if the user has not focused elsewhere.
    if (!disabled && wasDisabledRef.current && active === body && root?.contains(focusedRef.current)) focusedRef.current?.focus();
    wasDisabledRef.current = disabled;
  }, [disabled]);

  return (
    <SaveBoundaryContext.Provider value={disabled}>
      <div
        ref={rootRef}
        className={className}
        inert={disabled || undefined}
        data-saving={disabled || undefined}
        onFocusCapture={(event) => {
          if (event.target instanceof HTMLElement) focusedRef.current = event.target;
        }}
        onBlurCapture={(event) => {
          if (event.relatedTarget instanceof Node && !event.currentTarget.contains(event.relatedTarget)) focusedRef.current = null;
        }}
      >
        <fieldset className="save-boundary__fieldset" disabled={disabled}>
          {children}
        </fieldset>
      </div>
    </SaveBoundaryContext.Provider>
  );
};

export type SavedFormState<TResult> = {
  isSaving: boolean;
  error: string | null;
  lastResult: TResult | null;
};

export type SavedFormProps<TValues, TResult> = {
  values: TValues;
  onSave: (values: TValues) => TResult | Promise<TResult>;
  onSaved?: (result: TResult) => void | Promise<void>;
  onError?: (error: unknown) => string;
  savingLabel?: string;
  children: (state: SavedFormState<TResult>) => ReactNode;
  className?: string;
  noValidate?: boolean;
};

const defaultErrorMessage = (error: unknown): string => (error instanceof Error ? error.message : "Save failed. Try again.");

export const SavedForm = <TValues, TResult>({ values, onSave, onSaved, onError = defaultErrorMessage, savingLabel = "Saving…", children, className, noValidate }: SavedFormProps<TValues, TResult>) => {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<TResult | null>(null);
  const pendingRef = useRef(false);
  const generationRef = useRef(0);

  useEffect(
    () => () => {
      generationRef.current += 1;
    },
    [],
  );

  const submit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (pendingRef.current) return;

    pendingRef.current = true;
    const generation = ++generationRef.current;
    setIsSaving(true);
    setError(null);
    setLastResult(null);

    try {
      const result = await onSave(values);
      await onSaved?.(result);
      if (generationRef.current !== generation) return;
      setLastResult(result);
    } catch (caughtError) {
      if (generationRef.current !== generation) return;
      setError(onError(caughtError));
    } finally {
      if (generationRef.current === generation) {
        pendingRef.current = false;
        setIsSaving(false);
      }
    }
  };

  return (
    <form className={className} noValidate={noValidate} aria-busy={isSaving} onSubmit={submit}>
      <SaveBoundary saving={isSaving}>{children({ isSaving, error, lastResult })}</SaveBoundary>
      {isSaving && (
        <p className="t-caption" role="status">
          {savingLabel}
        </p>
      )}
      {error && (
        <p className="feedback feedback--error t-body-sm" role="alert">
          {error}
        </p>
      )}
    </form>
  );
};
